import React, { useRef, useEffect } from 'react';
import { renderAsset } from './utils/renderAsset.ts';

interface AssetRendererProps {
  pixiContainer: React.RefObject<HTMLDivElement>;
  assets: any[] | undefined;
  width: number;
  height: number;
  onUpdateAsset: (
    index: number,
    properties: { position?: { x: number; y: number }; scale?: number }
  ) => void;
}

interface AssetRef {
  container: HTMLDivElement;
  url: string;
  isDragging: boolean;
  position: { x: number; y: number };
  scale: number;
}

const AssetRenderer: React.FC<AssetRendererProps> = ({
  pixiContainer,
  assets,
  width,
  height,
  onUpdateAsset,
}) => {
  const assetsRef = useRef<Map<number, AssetRef>>(new Map());
  const activeAssetRef = useRef<{ index: number; ref: AssetRef | null }>({
    index: -1,
    ref: null,
  });

  const dragStateRef = useRef<{
    isDragging: boolean;
    startPos: { x: number; y: number };
  }>({
    isDragging: false,
    startPos: { x: 0, y: 0 },
  });

  useEffect(() => {
    if (!assets || !pixiContainer.current) return;

    const cleanupFunctions: Array<() => void> = [];

    assets.forEach((asset, index) => {
      if (!assetsRef.current.has(index)) {
        const cleanup = renderAsset(
          asset,
          index,
          width,
          height,
          onUpdateAsset,
          pixiContainer,
          assetsRef,
          activeAssetRef,
          dragStateRef
        );
        cleanupFunctions.push(cleanup);
      }
    });

    assetsRef.current.forEach((asset, index) => {
      if (index >= assets.length) {
        asset.container.remove();
        assetsRef.current.delete(index);
      }
    });

    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
    };
  }, [assets, width, height]);

  return null;
};

export default AssetRenderer;
